import React, { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { NETWORKING_MAIN_TAB_ID } from '../components/networking/networkingCatalogReturn';
import {
  fetchNetworkingCatalog,
  getCatalogSupportDownloadUrl,
  type InitiativeCatalogRow,
} from '../components/networking/networkingApi';
import '../components/networking/NetworkingReservations.css';
import './InitiativeCatalogPage.css';

const INITIATIVE_DOC_TITLE = 'Catálogo de Iniciativas';

function safeDownloadFilename(name: string): string {
  const base = String(name || '').trim().slice(0, 180) || 'documento';
  return base.replace(/[/\\?%*:|"<>]/g, '_').replace(/\s+/g, ' ').trim() || 'documento';
}

function InitiativeCatalogPage() {
  const [searchParams] = useSearchParams();
  const showReturnToBooking = searchParams.get('returnBooking') === '1';
  const [catalogQ, setCatalogQ] = useState('');
  const [catalogRows, setCatalogRows] = useState<InitiativeCatalogRow[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadCatalog = useCallback(async (q: string) => {
    setCatalogLoading(true);
    setError(null);
    try {
      const r = await fetchNetworkingCatalog(q);
      setCatalogRows(r.data);
    } catch (e: unknown) {
      setCatalogRows([]);
      setError(e instanceof Error ? e.message : 'No se pudo cargar el catálogo');
    } finally {
      setCatalogLoading(false);
    }
  }, []);

  useEffect(() => {
    const prev = document.title;
    document.title = INITIATIVE_DOC_TITLE;
    return () => {
      document.title = prev;
    };
  }, []);

  useEffect(() => {
    const delay = catalogQ.trim() ? 300 : 0;
    const t = window.setTimeout(() => {
      void loadCatalog(catalogQ.trim());
    }, delay);
    return () => window.clearTimeout(t);
  }, [catalogQ, loadCatalog]);

  return (
    <div className="profile-networking-embed net-catalog-standalone">
      <header className="net-catalog-standalone__header">
        <div className="net-catalog-standalone__head-inner">
          {showReturnToBooking ? (
            <Link to={`/?tab=${NETWORKING_MAIN_TAB_ID}`} className="net-catalog-standalone__back">
              ← Volver al perfil
            </Link>
          ) : null}
          <h1 className="net-catalog-standalone__title">{INITIATIVE_DOC_TITLE}</h1>
        </div>
      </header>

      <main className="net-catalog-standalone__main">
        {error && <div className="auth-message error net-alert">{error}</div>}

        <div className="form-field net-initiatives-filter">
          <label htmlFor="catalog-page-q">Filtrar (opcional)</label>
          <input
            id="catalog-page-q"
            className="form-input"
            placeholder="Nombre de iniciativa, organización, correo o teléfono de contacto…"
            value={catalogQ}
            onChange={(e) => setCatalogQ(e.target.value)}
            autoComplete="off"
          />
        </div>

        {catalogLoading && <p className="net-muted net-muted--tight">Cargando iniciativas…</p>}
        {!catalogLoading && catalogRows.length === 0 && !error && (
          <p className="net-muted net-muted--tight">No hay iniciativas para mostrar.</p>
        )}

        <div className="net-initiative-list" role="list" aria-label="Iniciativas del catálogo">
          {catalogRows.map((c) => {
            const contactPerson = [c.contact_first_name, c.contact_last_name]
              .filter(Boolean)
              .join(' ')
              .trim();
            const role = (c.contact_position || '').trim();
            const contactPhone = (c.contact_phone || '').trim();
            const hasContact = !!(contactPerson || role || (c.contact_email || '').trim() || contactPhone);
            return (
              <article
                key={`${c.owner_user_id}-${c.initiative_client_id}`}
                className="net-initiative-card net-initiative-card--readonly net-initiative-card--catalog"
                role="listitem"
              >
                <div className="net-initiative-card-stack">
                  <div className="net-initiative-card-head">
                    {c.type_label ? (
                      <div className="net-initiative-card-badge-row">
                        <span className="net-initiative-card-badge">{c.type_label}</span>
                      </div>
                    ) : null}
                    <h3 className="net-initiative-card-title">{c.name}</h3>
                  </div>
                  {c.organization_name ? (
                    <span className="net-initiative-card-org">{c.organization_name}</span>
                  ) : null}
                  {c.short_description ? (
                    <p className="net-initiative-card-desc">{c.short_description}</p>
                  ) : null}
                  {c.support_documents && c.support_documents.length > 0 ? (
                    <div
                      className="net-catalog-docs"
                      aria-label="Documentos de apoyo de la iniciativa"
                    >
                      <h4 className="net-catalog-contact-heading">Documentos de apoyo</h4>
                      <ul className="net-catalog-docs-list">
                        {c.support_documents.map((doc, docIdx) => (
                          <li key={`${doc.download_token}-${docIdx}`} className="net-catalog-docs-item">
                            <a
                              href={getCatalogSupportDownloadUrl(doc.download_token)}
                              className="net-catalog-docs-link"
                              download={safeDownloadFilename(doc.name)}
                              title={`Descargar: ${doc.name}`}
                            >
                              {doc.name}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                  <div
                    className="net-initiative-card-contact net-initiative-card-contact--catalog-stack"
                    aria-label="Datos de contacto de la iniciativa"
                  >
                    <h4 className="net-catalog-contact-heading">Datos de contacto de la iniciativa</h4>
                    {!hasContact ? (
                      <p className="net-catalog-contact-empty">Sin datos de contacto en la iniciativa</p>
                    ) : (
                      <>
                        {contactPerson ? (
                          <div className="net-catalog-contact-item">
                            <span className="net-catalog-contact-label">Nombre</span>
                            <span className="net-catalog-contact-value">{contactPerson}</span>
                          </div>
                        ) : null}
                        {role ? (
                          <div className="net-catalog-contact-item">
                            <span className="net-catalog-contact-label">Cargo</span>
                            <span className="net-catalog-contact-value">{role}</span>
                          </div>
                        ) : null}
                        {c.contact_email ? (
                          <div className="net-catalog-contact-item">
                            <span className="net-catalog-contact-label">Correo</span>
                            <span className="net-catalog-contact-value">{c.contact_email}</span>
                          </div>
                        ) : null}
                        {contactPhone ? (
                          <div className="net-catalog-contact-item">
                            <span className="net-catalog-contact-label">Teléfono</span>
                            <span className="net-catalog-contact-value">{contactPhone}</span>
                          </div>
                        ) : null}
                      </>
                    )}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </main>
    </div>
  );
}

export default InitiativeCatalogPage;
